// Client-side cart model for optimistic UI updates.
// Tracks items/totals/counts so we can update peripheral UI instantly
// without parsing DOM or waiting for server responses.

import {
	isMembershipCartItem,
	isBundleCartItem,
	isStandaloneTrialCartItem,
} from '@/scripts/core/cart-functions'
import type { Cart, CartItem } from '@/scripts/shopify'

export type CartStateItem = {
	key: string | null // server-assigned, null for optimistic items
	variantId: number
	productId: number
	quantity: number
	price: number // unit price in cents (payable amount)
	compareAtPrice: number | null // unit compare-at price in cents (for display subtotal)
	title: string
	variantTitle: string | null
	imageUrl: string | null
	productUrl: string
	productType: string
	properties: Record<string, string>
	isMembership: boolean
	isGwp: boolean
	isBundle: boolean
	isStandaloneTrial: boolean
}

export type CartTotals = {
	displaySubtotal: number // cents (compare-at based, for subtotal display)
	itemCount: number
	totalPrice: number // cents (payable amount, for checkout button)
}

type CartState = {
	items: CartStateItem[]
	totals: CartTotals
}

let _state: CartState = {
	items: [],
	totals: { displaySubtotal: 0, itemCount: 0, totalPrice: 0 },
}
let _initialized = false

function cartItemToStateItem(item: CartItem): CartStateItem {
	return {
		key: item.key,
		variantId: item.id,
		productId: item.product_id,
		quantity: item.quantity,
		price: item.price,
		compareAtPrice:
			item.original_price > item.price ? item.original_price : null,
		title: item.product_title,
		variantTitle: item.variant_title,
		imageUrl: item.image ?? null,
		productUrl: item.url,
		productType: item.product_type ?? '',
		properties: item.properties ?? {},
		isMembership: isMembershipCartItem(item),
		isGwp: Boolean(item.properties?._gwp_item),
		isBundle: isBundleCartItem(item),
		isStandaloneTrial: isStandaloneTrialCartItem(item),
	}
}

export function computeTotals(items: CartStateItem[]): CartTotals {
	let displaySubtotal = 0
	let payableTotal = 0
	let itemCount = 0
	for (const item of items) {
		// Use compareAtPrice for display subtotal if available, otherwise use price
		const unitDisplayPrice = item.compareAtPrice ?? item.price
		displaySubtotal += unitDisplayPrice * item.quantity
		payableTotal += item.price * item.quantity
		itemCount += item.quantity
	}
	return { displaySubtotal, itemCount, totalPrice: payableTotal }
}

/** Hydrate CartState from a Shopify Cart JSON response. */
export function initCartState(cart: Cart): void {
	const items = cart.items.map(cartItemToStateItem)
	_state = {
		items,
		totals: computeTotals(items),
	}
	_initialized = true
}

/** Return current client-side cart state. */
export function getCartState(): CartState {
	return _state
}

/** Whether CartState has been hydrated from a server response. */
export function isCartStateInitialized(): boolean {
	return _initialized
}

/**
 * Optimistic exclusive-item eviction.
 * When adding a bundle or membership, remove all existing exclusive items
 * (bundles, memberships, their associated GWPs) from client state.
 * Returns the 1-based line indices that were removed (for DOM collapse).
 */
export function evictExclusiveItems(): {
	state: CartState
	removedIndices: number[] // 1-based
} {
	const removedIndices: number[] = []
	const items = _state.items.filter((item, i) => {
		const shouldRemove =
			item.isMembership || item.isBundle || item.isStandaloneTrial
		if (shouldRemove) {
			removedIndices.push(i + 1)
			return false
		}
		return true
	})
	_state = { items, totals: computeTotals(items) }
	return { state: _state, removedIndices }
}

/** Optimistic add — append items and recompute totals. */
export function applyAdd(newItems: CartStateItem[]): CartState {
	_state = {
		items: [..._state.items, ...newItems],
		totals: computeTotals([..._state.items, ...newItems]),
	}
	return _state
}

/** Optimistic quantity change (1-based lineIndex from Shopify). */
export function applyQuantityChange(
	lineIndex: number,
	newQty: number
): CartState {
	const idx = lineIndex - 1
	if (idx < 0 || idx >= _state.items.length) return _state

	const items = _state.items.map((item, i) => {
		if (i === idx) return { ...item, quantity: newQty }
		return item
	})

	_state = { items, totals: computeTotals(items) }
	return _state
}

/**
 * Optimistic remove (1-based lineIndex).
 * Also cascades: removing a bundle/product removes its associated membership
 * and GWP (detected via _bundle_parent_id matching the removed product_id).
 * Returns the updated state, the 1-based indices that were removed, and the
 * server-assigned keys of all removed items (for batched server removal).
 */
export function applyRemove(lineIndex: number): {
	state: CartState
	removedIndices: number[] // 1-based
	removedKeys: string[] // server-assigned cart line keys
} {
	const idx = lineIndex - 1
	if (idx < 0 || idx >= _state.items.length) {
		return { state: _state, removedIndices: [], removedKeys: [] }
	}

	const removedItem = _state.items[idx]
	const removedIndices = [lineIndex]
	const removedKeys: string[] = []
	if (removedItem.key) removedKeys.push(removedItem.key)

	// Cascade: removing a product also removes its linked membership/GWP.
	// _bundle_parent_id is set on memberships and GWP items, pointing to the trigger product.
	// Use String() on both sides — Shopify may serialize the property as a number.
	const cascadeProductId = String(removedItem.productId)
	const items = _state.items.filter((item, i) => {
		if (i === idx) return false
		if (
			String(item.properties._bundle_parent_id) === cascadeProductId &&
			(item.isMembership || item.isGwp)
		) {
			removedIndices.push(i + 1)
			if (item.key) removedKeys.push(item.key)
			return false
		}
		return true
	})

	_state = { items, totals: computeTotals(items) }
	return { state: _state, removedIndices, removedKeys }
}

/** Replace client state with authoritative server cart. */
export function reconcile(serverCart: Cart): CartState {
	initCartState(serverCart)
	return _state
}
